import { create } from "zustand";
import { useEffect } from "react";
import { CoinService } from "./coin.service";
import {
	TrialService,
	type TrialCoinWindow,
} from "../subscription/trial.service";

interface CoinStore {
	balance: number | null;
	trialWindow: TrialCoinWindow | null;
	isLoading: boolean;
	error: string | null;
	lastFetched: Date | null;

	// Actions
	fetchBalance: () => Promise<void>;
	refreshBalance: () => Promise<void>;
	reset: () => void;
}

async function loadCoinState(force: boolean): Promise<void> {
	if (!force && useCoinStore.getState().isLoading) return;

	useCoinStore.setState({ isLoading: true, error: null });
	try {
		const [balanceResult, trialResult] = await Promise.allSettled([
			CoinService.getBalance(),
			TrialService.getCoinWindow(),
		]);

		const next: Partial<CoinStore> = {
			isLoading: false,
			lastFetched: new Date(),
		};

		if (balanceResult.status === "fulfilled") {
			next.balance = balanceResult.value.balance;
		} else {
			console.error("Failed to fetch coin balance:", balanceResult.reason);
			next.error =
				balanceResult.reason instanceof Error
					? balanceResult.reason.message
					: "Failed to fetch balance";
		}

		if (trialResult.status === "fulfilled") {
			next.trialWindow = trialResult.value;
		} else {
			console.error("Failed to fetch trial coin window:", trialResult.reason);
		}

		useCoinStore.setState(next);
	} catch (error) {
		console.error("Failed to fetch coin state:", error);
		useCoinStore.setState({
			error: error instanceof Error ? error.message : "Failed to fetch balance",
			isLoading: false,
		});
	}
}

export const useCoinStore = create<CoinStore>((set) => ({
	balance: null,
	trialWindow: null,
	isLoading: false,
	error: null,
	lastFetched: null,

	fetchBalance: () => loadCoinState(false),
	refreshBalance: () => loadCoinState(true),

	reset: () => {
		set({
			balance: null,
			trialWindow: null,
			isLoading: false,
			error: null,
			lastFetched: null,
		});
	},
}));

// Helper hook to get balance with auto-fetch
export const useCoinBalance = () => {
	const {
		balance,
		trialWindow,
		isLoading,
		error,
		fetchBalance,
		refreshBalance,
	} = useCoinStore();

	// Auto-fetch on mount if balance is not available
	useEffect(() => {
		if (balance === null && !isLoading && !error) {
			fetchBalance();
		}
	}, [balance, isLoading, error, fetchBalance]);

	return {
		balance,
		trialWindow,
		isLoading,
		error,
		refresh: refreshBalance,
	};
};

// Export for convenience
export { CoinService } from "./coin.service";
